import React, { useState } from 'react';
import { readFile, uploadFileRequest } from '../../lib/api/public';
import { Button, CircularProgress, Table } from '@mui/material';
import DeleteButton from './DeleteButton';
import notify from 'lib/notify';
import { sortFilesByDate } from '../utils';
import { IAttachment } from '../types';
import Link from 'components/common/Link';

const getUploadErrorMessage = async (response: Response) => {
  const text = await response.text();

  if (response.status === 413 || /content too large|request entity too large/i.test(text)) {
    return 'File too large. The maximum size is 50 MB.';
  }

  if (text.trim()) {
    try {
      const parsed = JSON.parse(text) as { message?: string };
      if (typeof parsed.message === 'string' && parsed.message.trim()) {
        return parsed.message;
      }
    } catch {
      // ignore non-JSON responses (e.g. nginx/html)
    }
  }

  return `Upload failed (${response.status})`;
};

type Props = {
  label: string;
  onChange: (results: IAttachment | IAttachment[]) => void;
  multiple?: boolean;
  showList?: boolean;
  defaultFiles?: IAttachment[];
  isPublic?: boolean;
  bucketName?: string;
  onStartUploading?: () => void;
  onFinishUploading?: () => void;
  accept?: string;
  testId?: string;
};

const Uploader = ({
  label,
  onChange,
  multiple = true,
  showList = true,
  defaultFiles,
  isPublic,
  bucketName,
  onStartUploading,
  onFinishUploading,
  accept,
  testId,
}: Props) => {
  const [uploadingCount, setUploadingCount] = useState(0);
  const [results, setResults] = useState(defaultFiles || []);

  const getResults = () => {
    return sortFilesByDate(results);
  };

  const uploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    onStartUploading?.();

    if (!files || files.length === 0) {
      onFinishUploading?.();
      return;
    }

    for (const file of Array.from(files)) {
      // Check file type if accept is specified
      if (accept) {
        const fileExtension = file.name.toLowerCase().split('.').pop();
        const acceptedExtensions = accept.split(',').map(ext => ext.trim().replace('.', ''));

        if (!fileExtension || !acceptedExtensions.includes(fileExtension)) {
          notify(`Invalid file type. Please upload a ${accept} file.`);
          continue;
        }
      }

      const formData = new FormData();
      formData.append('file', file);

      setUploadingCount(prev => prev + 1);

      try {
        const response = await uploadFileRequest({ formData, bucketName, isPublic });

        if (!response.ok) {
          notify(new Error(await getUploadErrorMessage(response)));
          continue;
        }

        const { result } = (await response.json()) as { result: string };

        const newResult = {
          url: result,
          date: new Date(),
          name: file.name,
        };

        results.push(newResult);

        setResults([...results]);

        if (multiple) {
          onChange(getResults());
        } else {
          onChange(newResult);
        }
      } catch (error) {
        if (error instanceof TypeError || (error instanceof Error && error.message === 'Failed to fetch')) {
          notify(new Error('Could not upload the file.'));
          continue;
        }

        notify(error instanceof Error ? error : new Error('Upload failed'));
      } finally {
        setUploadingCount(prev => prev - 1);
      }
    }

    e.target.value = '';

    onFinishUploading?.();
  };

  const removeResult = (result: IAttachment) => {
    const newResults = results.filter(r => r.url !== result.url);

    setResults(newResults);

    if (multiple) {
      onChange(newResults);
    } else {
      onChange(newResults[0]);
    }
  };

  const renderList = () => {
    if (!showList) {
      return null;
    }

    const results = getResults();

    return (
      <>
        {results.length > 0 ? (
          <>
            <br />
            <br />
          </>
        ) : null}

        <Table size="small">
          {results.map((result, index) => {
            const fileName = result.name || 'Untitled file';
            const fileUrl = result.url;

            return (
              <div style={{ display: 'flex', alignItems: 'center' }} key={index}>
                {fileUrl ? (
                  <Link href={readFile(fileUrl)} target="__blank">
                    {fileName.length > 50 ? fileName.substring(0, 50) + '...' : fileName}
                  </Link>
                ) : (
                  <span>{fileName.length > 50 ? fileName.substring(0, 50) + '...' : fileName}</span>
                )}

                <DeleteButton onClick={() => removeResult(result)} />
              </div>
            );
          })}
        </Table>
      </>
    );
  };

  const id = `uploader-${label}-${Math.random()}`;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <input
        type="file"
        id={id}
        onChange={uploadFile}
        style={{ display: 'none' }}
        multiple={multiple}
        accept={accept}
      />

      <Button
        variant="contained"
        color="primary"
        data-testid={testId}
        onClick={() => {
          const input = document.getElementById(id);

          if (input instanceof HTMLInputElement) {
            input.click();
          }
        }}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {label}
        {uploadingCount > 0 ? <CircularProgress color="secondary" size={20} sx={{ marginLeft: '10px' }} /> : null}
      </Button>

      {renderList()}
    </div>
  );
};

export default Uploader;
